import React, { ReactElement } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import SortSelect from 'app/components/misc/SortSelect';
import {
  PRODUCT_FILTER_TYPE,
  PRODUCT_FILTER_TRACKING_MAP,
  PRODUCT_FILTER_TYPE_LIST,
  PRODUCT_FILTER_TYPE_LIST_WITHOUT_POPULAR,
  PRODUCT_FILTER_TYPE_MAP,
} from 'app/constants/product';
import { Checkbox } from 'app/seedly-component-library';
import { getCurrentCategory } from 'app/selectors/productSelector';
import { useSelector, useDispatch } from 'react-redux';
import { getProducts } from 'app/dispatcher/productDispatcher';
import ProductCategorySelect from 'app/components/product/ProductCategorySelect';
import mixpanel, { trackedEvents } from 'app/utils/mixpanel';
import analytics from 'app/utils/analytics';

import type { RootState } from 'app/reducer';

const ProductListSort = ({
  isSignUpBonusChecked,
  handleSignUpBonusChange,
  handleFilterClick,
  type,
}) => {
  const currentCategory = useSelector(getCurrentCategory);
  const { categorySlug, term } = useSelector(
    (state: RootState) => state.product.meta,
  );
  const productFilter = useSelector(
    (state: RootState) => state.product.productFilterParams.filter,
  );
  const isSignUpBonus = useSelector(
    (state: RootState) => state.product.meta.isSignUpBonus,
  );
  const isSignUpBonusType = type === 'sign-up-bonus';

  const router = useRouter();
  const dispatch = useDispatch();

  const handleOnSelect = filter => {
    if (PRODUCT_FILTER_TYPE_LIST_WITHOUT_POPULAR.includes(filter)) {
      mixpanel.track(trackedEvents.ReviewHome, {
        param: PRODUCT_FILTER_TRACKING_MAP[filter],
      });
    }

    analytics.trackSortingChange({
      key: filter,
      value: PRODUCT_FILTER_TRACKING_MAP[filter],
    });

    const params = {
      categorySlug,
      sortType: filter,
      term,
      productFilter,
      isSignUpBonus,
    };

    // @ts-ignore until getProducts is converted to Typescript
    dispatch(getProducts(params));
  };

  const hasCurrentCategory = !!currentCategory;
  const options = hasCurrentCategory
    ? PRODUCT_FILTER_TYPE_LIST
    : PRODUCT_FILTER_TYPE_LIST_WITHOUT_POPULAR;

  let filterOptions: Record<string, string | ReactElement>;

  if (hasCurrentCategory) {
    filterOptions = PRODUCT_FILTER_TYPE_MAP;
  } else {
    filterOptions = PRODUCT_FILTER_TYPE_MAP;
    delete filterOptions[PRODUCT_FILTER_TYPE.MOST_POPULAR];
  }

  return (
    <div
      className={clsx(
        'flex flex-col items-center pb-3 w-full lg:flex-row-reverse',
      )}
    >
      <div
        className={clsx(
          'flex flex-col justify-between w-full gap-2 lg:flex-row lg:w-auto',
          !isSignUpBonusType && 'flex-col',
        )}
      >
        <SortSelect
          options={options}
          optionsName={filterOptions}
          onClick={handleOnSelect}
          handleFilterClick={handleFilterClick}
          isBlock={!!isSignUpBonusType}
        />
        {isSignUpBonusType && (
          <ProductCategorySelect
            onChange={item => {
              router.push(`/reviews/sign-up-bonus/${item.value}`);
            }}
          />
        )}
      </div>
      {/* if user is on reviews page and not the dedicated sign up bonus page */}
      {!isSignUpBonusType && (
        <div className="w-full">
          <Checkbox
            name="signup-bonus"
            checked={isSignUpBonusChecked}
            labelSpace="xs"
            onChange={event => {
              const isChecked = event.target.checked;
              const params = {
                categorySlug,
                term,
                productFilter,
                isSignUpBonus: isChecked,
              };
              if (isChecked) {
                mixpanel.track(trackedEvents.SignUpBonus, {
                  param: 'show_only_products_with_sign_up_bonus',
                });
              }
              // @ts-ignore until getProducts is converted to typescript
              dispatch(getProducts(params));
              if (handleSignUpBonusChange) {
                handleSignUpBonusChange(event);
              }
            }}
          >
            <p>
              Show only products with{' '}
              <span className="whitespace-pre">Sign Up Bonus</span>
            </p>
          </Checkbox>
        </div>
      )}
    </div>
  );
};

export default ProductListSort;
